import React, { useEffect, useState } from "react";
import FormHeading from "../../components/FormHeading";
import CustomInput from "../../components/CustomInput";
import ErrorText from "../../components/ErrorText";
import Loader from "../../components/Loader";
import { useQuery } from "@tanstack/react-query";
import { getSuburbs } from "../../api/StaticsApi";
import { AddressType } from "../../interfaces/Address";
import { Suburb } from "../../interfaces/Suburb";
import { isEmpty } from "../../utils/Common";

type AddressProps = {
  data: AddressType;
  changeValue: (value: string, field: keyof AddressType) => void;
  setIsAddressValid: (isValid: boolean) => void;
};

const Address = ({
  data: { address1, address2, address3, postalCode, suburb },
  setIsAddressValid,
  changeValue,
}: AddressProps) => {
  const [addressStartsWithANumber, setAddressStartsWithANumber] =
    useState<boolean>(true);
  const [
    postalCodeHasMoreThan4Characters,
    setPostalCodeHasMoreThan4Characters,
  ] = useState<boolean>(true);

  const {
    data: suburbs,
    isLoading,
    isError,
  } = useQuery<Suburb[]>({
    queryKey: ["suburbs", postalCode, suburb],
    queryFn: () => getSuburbs(postalCode, suburb),
    enabled: !!postalCode && suburb.length >= 3,
  });

  useEffect(() => {
    if (address1.length > 1)
      if (/[0-9]/.test(address1.slice(0, 1))) {
        setAddressStartsWithANumber(true);
      } else {
        setAddressStartsWithANumber(false);
      }
  }, [address1]);

  useEffect(() => {
    if (postalCode.length > 1)
      if (postalCode.length >= 4) {
        setPostalCodeHasMoreThan4Characters(true);
      } else {
        setPostalCodeHasMoreThan4Characters(false);
      }
  }, [postalCode]);

  const isSuburbValid = (suburb: string) => {
    return !isEmpty(
      suburbs?.find(
        (s) => s.postCode === Number(postalCode) && s.suburbName === suburb
      )?.suburbName || null
    );
  };

  useEffect(() => {
    setIsAddressValid(
      address1 !== "" &&
        postalCode.length >= 4 &&
        isSuburbValid(suburb) &&
        addressStartsWithANumber !== false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address1, postalCode, suburb, suburbs, addressStartsWithANumber]);

  return (
    <>
      <FormHeading>Address</FormHeading>
      <CustomInput
        name="address1"
        placeholder="Address Details 1"
        value={address1}
        onChange={(value: string) => changeValue(value, "address1")}
        max={40}
        required
      />
      {addressStartsWithANumber === false && (
        <ErrorText>Address must start with a number!</ErrorText>
      )}
      <CustomInput
        name="address2"
        placeholder="Address Details 2"
        value={address2}
        onChange={(value: string) => changeValue(value, "address2")}
        max={40}
      />
      <CustomInput
        name="address3"
        placeholder="Address Details 3"
        value={address3}
        onChange={(value: string) => changeValue(value, "address3")}
        max={40}
      />
      <CustomInput
        name="postalCode"
        placeholder="Postal Code"
        value={postalCode}
        onChange={(value: string) => changeValue(value, "postalCode")}
        type="number"
        max={40}
        required
      />
      {postalCodeHasMoreThan4Characters === false && (
        <ErrorText>Post code should be at least 4 characters!</ErrorText>
      )}
      <CustomInput
        name="suburb"
        placeholder="Search Suburb"
        value={suburb}
        onChange={(value: string) => changeValue(value, "suburb")}
        max={40}
        required
      />
      {isLoading && <Loader />}
      {isError && (
        <ErrorText>Failed loading suburbs. Please try again later!</ErrorText>
      )}

      {suburbs && (
        <>
          {suburbs.length === 0 ? (
            <ErrorText>
              {`No Suburbs can be found for postal code ${postalCode} and search
              value: ${suburb}`}
            </ErrorText>
          ) : (
            <>
              <FormHeading>Please choose your correct Suburb!</FormHeading>
              <div className="grid grid-cols-2 gap-4">
                {suburbs.map((suburb) => (
                  <div
                    key={suburb.sequenceNumber}
                    className="p-4 bg-grayBG"
                    onClick={() => changeValue(suburb.suburbName, "suburb")}
                  >
                    {suburb.suburbName}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Address;
